/**
 * SessionManagement - CRUD for event sessions/schedule
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar, Clock, MapPin, Users, Loader2, Sparkles } from 'lucide-react';
import { sessionAPI, speakerAPI } from '@/lib/api';
import SessionAIPanel from './SessionAIPanel';
import { DateTimePicker } from './TimePicker';

const TYPES = ['keynote', 'workshop', 'panel', 'breakout', 'plenary', 'combined', 'networking', 'break', 'general'];
const STATUSES = ['draft', 'published', 'cancelled'];
const TYPE_COLORS = { 
  keynote: 'bg-purple-100 text-purple-800', 
  workshop: 'bg-blue-100 text-blue-800', 
  panel: 'bg-green-100 text-green-800', 
  breakout: 'bg-orange-100 text-orange-800', 
  plenary: 'bg-red-100 text-red-800',
  combined: 'bg-indigo-100 text-indigo-800',
  networking: 'bg-pink-100 text-pink-800', 
  break: 'bg-gray-100 text-gray-800',
  general: 'bg-slate-100 text-slate-800'
};

// Form component extracted outside
const SessionForm = ({ session, speakers, onSubmit, onCancel }) => {
  const [form, setForm] = useState(session || { title: '', description: '', session_type: 'breakout', track: '', room: '', start_time: '', end_time: '', speaker_ids: [], capacity: '', status: 'draft', tags: [] });
  const formatDateTime = (dt) => dt ? new Date(dt).toISOString().slice(0, 16) : '';
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Type</Label>
          <Select value={form.session_type} onValueChange={v => setForm({...form, session_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Status</Label>
          <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={3} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div><Label>Track</Label><Input value={form.track || ''} onChange={e => setForm({...form, track: e.target.value})} placeholder="e.g., Tech, Business" /></div>
        <div><Label>Room</Label><Input value={form.room || ''} onChange={e => setForm({...form, room: e.target.value})} placeholder="Room A" /></div>
        <div><Label>Capacity</Label><Input type="number" value={form.capacity || ''} onChange={e => setForm({...form, capacity: parseInt(e.target.value) || ''})} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <DateTimePicker label="Start Time" value={formatDateTime(form.start_time)} onChange={v => setForm({...form, start_time: v})} />
        <DateTimePicker label="End Time" value={formatDateTime(form.end_time)} onChange={v => setForm({...form, end_time: v})} />
      </div>
      <div><Label>Speakers</Label>
        <Select value="" onValueChange={v => !form.speaker_ids.includes(v) && setForm({...form, speaker_ids: [...form.speaker_ids, v]})}>
          <SelectTrigger><SelectValue placeholder="Add speakers..." /></SelectTrigger>
          <SelectContent>{speakers.filter(s => !form.speaker_ids.includes(s.id)).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex flex-wrap gap-1 mt-2">{form.speaker_ids.map(id => { const sp = speakers.find(s => s.id === id); return sp ? <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => setForm({...form, speaker_ids: form.speaker_ids.filter(i => i !== id)})}>{sp.name} ×</Badge> : null; })}</div>
      </div>
      <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.title}>{session ? 'Update' : 'Create'}</Button>
      </div>
    </div>
  );
};

const SessionManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [aiSession, setAiSession] = useState(null);
  const [filterTrack, setFilterTrack] = useState('all');

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions', eventId, filterTrack],
    queryFn: () => sessionAPI.list(eventId, filterTrack === 'all' ? undefined : filterTrack).then(r => {
      const data = r.data;
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      return [];
    }),
  });

  const { data: speakers = [] } = useQuery({
    queryKey: ['speakers', eventId],
    queryFn: () => speakerAPI.list(eventId).then(r => {
      const data = r.data;
      if (Array.isArray(data)) return data;
      if (data?.data && Array.isArray(data.data)) return data.data;
      return [];
    }),
  });

  const tracks = [...new Set(sessions.map(s => s.track).filter(Boolean))];

  const createMutation = useMutation({
    mutationFn: (data) => sessionAPI.create(eventId, data),
    onSuccess: () => { toast.success('Session created'); queryClient.invalidateQueries(['sessions', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => sessionAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Session updated'); queryClient.invalidateQueries(['sessions', eventId]); setEditingSession(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => sessionAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Session deleted'); queryClient.invalidateQueries(['sessions', eventId]); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed')
  });

  const formatTime = (dt) => dt ? new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString() : '';

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="session-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Calendar className="h-6 w-6" />Sessions</h2>
          <Select value={filterTrack} onValueChange={setFilterTrack}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Tracks</SelectItem>{tracks.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Badge variant="outline">{sessions.length} sessions</Badge>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Session</Button></DialogTrigger>
          <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Add Session</DialogTitle></DialogHeader>
            <SessionForm speakers={speakers} onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {sessions.map(session => (
          <Card key={session.id} data-testid={`session-${session.id}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={TYPE_COLORS[session.session_type]}>{session.session_type}</Badge>
                    {session.track && <Badge variant="outline">{session.track}</Badge>}
                    <Badge variant={session.status === 'published' ? 'default' : 'secondary'}>{session.status}</Badge>
                  </div>
                  <h3 className="font-semibold text-lg">{session.title}</h3>
                  {session.description && <p className="text-sm text-slate-600 line-clamp-1">{session.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    {session.start_time && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{formatDate(session.start_time)} {formatTime(session.start_time)} - {formatTime(session.end_time)}</span>}
                    {session.room && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{session.room}</span>}
                    {session.capacity && <span className="flex items-center gap-1"><Users className="h-4 w-4" />{session.registered_count || 0}/{session.capacity}</span>}
                  </div>
                  {session.speaker_ids?.length > 0 && <div className="flex gap-1 mt-2">{session.speaker_ids.map(id => { const sp = speakers.find(s => s.id === id); return sp ? <Badge key={id} variant="outline" className="text-xs">{sp.name}</Badge> : null; })}</div>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setAiSession(session)} title="AI Tools"><Sparkles className="h-4 w-4 text-purple-600" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditingSession(session)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(session.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {sessions.length === 0 && <p className="text-center text-slate-500 py-8">No sessions scheduled yet.</p>}
      </div>

      <Dialog open={!!editingSession} onOpenChange={() => setEditingSession(null)}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Edit Session</DialogTitle></DialogHeader>
          {editingSession && <SessionForm session={editingSession} speakers={speakers} onSubmit={data => updateMutation.mutate({ id: editingSession.id, data })} onCancel={() => setEditingSession(null)} />}
        </DialogContent>
      </Dialog>

      {/* AI Panel Sheet */}
      <Sheet open={!!aiSession} onOpenChange={() => setAiSession(null)}>
        <SheetContent className="w-full max-w-[450px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              {aiSession?.title}
            </SheetTitle>
          </SheetHeader>
          {aiSession && <div className="mt-4"><SessionAIPanel eventId={eventId} session={aiSession} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SessionManagement;
